/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import CoTraderBarChart from "../CoTrader/CoTraderChart/CoTraderBarChart";
import "./CoHedger.css";
import logoMark from "../../../assets/logo-collapsed.png";
import CoHedgerSummaryTileComponent from "./CoHedgerSummaryTileComponent";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import { getAuthenticatedWebSocketUrl } from "../../utilities/authHelper";
import { FORWARD_TENOR_ORDER } from "../CoTrader/Utilities/forwardTenors";

const CurrencyList = {
  EURUSD: "EURUSD=X",
  USDJPY: "JPY=X",
  GBPUSD: "GBPUSD=X",
  USDCHF: "CHF=X",
  USDCAD: "CAD=X",
  AUDUSD: "AUDUSD=X",
  NZDUSD: "NZDUSD=X",
  USDNOK: "NOK=X",
  USDSEK: "SEK=X",
  USDUSD: "USD=X",
  USDINR: "USDINR=X",
};

const CoHedgerSummaryDashboardTile = ({
  context,
  currencyList,
  defaultCurrencyPair = "USDJPY",
  defaultAssetType = "STOCK",
  defaultExecutionType = "SELL",
  defaultExpirationDate = null,
  showForwardPoints = true,
  showLogo = false,
}) => {
  const { isDark } = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL || "";

  const [currencyPair, setCurrencyPair] = useState(defaultCurrencyPair);
  const [assetType, setAssetType] = useState(defaultAssetType);
  const [executionType, setExecutionType] = useState(defaultExecutionType);
  const [expirationDate, setExpirationDate] = useState(
    defaultExpirationDate ?? "None",
  );

  const [tenorList, setTenorList] = useState(FORWARD_TENOR_ORDER);
  const [isForwardCurveExpanded, setIsForwardCurveExpanded] = useState(false);

  const [refreshNonce, setRefreshNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");

  const wsRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!context) return;
    try {
      const ctxPair = context?.instruments?.[0]?.id?.ticker;
      if (!ctxPair) return;

      // Ignore pairs this tile can't price (not in its dropdown).
      const supportedPairs =
        Array.isArray(currencyList) && currencyList.length > 0
          ? currencyList
          : Object.keys(CurrencyList);
      if (supportedPairs.includes(ctxPair)) setCurrencyPair(ctxPair);
    } catch {}
  }, [context]);

  const requestBody = useMemo(() => {
    const user_id = localStorage.getItem("userId") || "ANON";
    const random_number = Math.floor(100000 + Math.random() * 900000);
    const chat_id = `HEDGE_ASSISTGPT-${user_id}-${random_number}`;

    return {
      currency_pair: currencyPair ?? null,
      yahoo_finance_tickers: CurrencyList[currencyPair] ?? null,
      asset_type: assetType ?? null,
      execution_type: executionType ?? null,
      chat_id,
      embedding_model: "text-embedding-ada-002",
      origin: "HEDGE_ASSIST",
      expiration_timestamp: expirationDate,
    };
  }, [currencyPair, assetType, executionType, expirationDate, refreshNonce]);

  useEffect(() => {
    if (!apiUrl) {
      console.error("REACT_APP_API_URL missing");
      return;
    }

    cancelledRef.current = true;
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }

    cancelledRef.current = false;
    setLoading(true);
    setSummary("");

    const user_id = localStorage.getItem("userId") || "ANON";
    const chat_id = requestBody.chat_id;
    let ws;
    let hasFinished = false;
    let reconnectEnabled = true;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    let activeAssistantMessageId = null;

    const stopStreaming = () => {
      reconnectEnabled = false;
      activeAssistantMessageId = null;
      setLoading(false);
    };

    const resetSummaryForReplay = () => {
      setSummary("");
    };

    const scheduleReconnect = () => {
      if (!reconnectEnabled || hasFinished || cancelledRef.current) return;
      if (!activeAssistantMessageId) {
        stopStreaming();
        return;
      }
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        stopStreaming();
        return;
      }
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
      reconnectAttempts += 1;
      window.setTimeout(() => {
        void openSocket(true).catch((error) => {
          console.error("WS reconnect auth failed", error);
          scheduleReconnect();
        });
      }, delay);
    };

    const openSocket = async (isReconnect = false) => {
      const wsUrl = await getAuthenticatedWebSocketUrl(
        chat_id,
        user_id,
        isReconnect ? activeAssistantMessageId : undefined,
      );
      if (cancelledRef.current) return;

      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isReconnect) {
          resetSummaryForReplay();
          return;
        }
        try {
          ws.send(JSON.stringify(requestBody));
        } catch (error) {
          console.error("WS send failed", error);
        }
      };

      ws.onmessage = (event) => {
        if (cancelledRef.current) return;

        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        activeAssistantMessageId =
          data?.message_id ||
          (data?.type === "MESSAGE_ID" && data?.message != null
            ? String(data.message)
            : activeAssistantMessageId);

        const messageType = data?.type;
        const message = data?.message;

        if (
          messageType === "FINISH" ||
          messageType === "DONE" ||
          messageType === "END" ||
          messageType === "COMPLETE"
        ) {
          hasFinished = true;
          stopStreaming();
          try {
            ws.close();
          } catch {}
          return;
        }

        if (
          messageType === "INFO" ||
          messageType === "ERROR" ||
          messageType === "MESSAGE_ID" ||
          messageType === "SUMMARY_UPDATE"
        ) {
          return;
        }

        if (message != null) {
          setSummary((previous) => previous + String(message));
        }
      };

      ws.onerror = (error) => {
        console.error("WS error", error);
      };

      ws.onclose = () => {
        if (!hasFinished && reconnectEnabled && !cancelledRef.current) {
          if (activeAssistantMessageId) {
            scheduleReconnect();
            return;
          }
          stopStreaming();
          return;
        }
        if (!cancelledRef.current) setLoading(false);
      };
    };

    void openSocket().catch((error) => {
      if (!cancelledRef.current) {
        console.error("WS auth failed", error);
        setLoading(false);
      }
    });

    return () => {
      cancelledRef.current = true;
      try {
        ws.close();
      } catch {}
    };
  }, [requestBody, apiUrl]);

  const handleStop = () => {
    cancelledRef.current = true;
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
    setLoading(false);
  };

  const handleRefresh = () => setRefreshNonce((previous) => previous + 1);

  const pairsToShow =
    Array.isArray(currencyList) && currencyList.length > 0
      ? currencyList
      : Object.keys(CurrencyList);

  return (
    <div className={`ch-page${isDark ? " is-dark" : ""}`}>
      <section className="ch-card ch-bar">
        <div className="ch-bar-head">
          <div>
            <p className="ch-eyebrow">
              {showLogo && <img src={logoMark} alt="EMAlpha" className="ch-logo" />}
              CoHedger
            </p>
            <h2 className="ch-title">Hedge Recommendation Desk</h2>
            <p className="ch-sub">
              One live hedge brief from the pair, execution side and expiry.
            </p>
          </div>

          <div className="ch-bar-actions">
            <span className={`ch-status${loading ? " is-live" : ""}`}>
              {loading ? "Streaming" : "Ready"}
            </span>
            {loading ? (
              <button type="button" className="ch-btn is-danger" onClick={handleStop}>
                Stop
              </button>
            ) : (
              <button type="button" className="ch-btn" onClick={handleRefresh}>
                Regenerate
              </button>
            )}
          </div>
        </div>

        <div className="ch-fields">
          <Form.Group className="ch-field">
            <Form.Label>Currency pair</Form.Label>
            <Form.Select value={currencyPair} onChange={(e) => setCurrencyPair(e.target.value)}>
              {pairsToShow.map((pair) => (
                <option key={pair} value={pair}>
                  {pair}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="ch-field">
            <Form.Label>Asset type</Form.Label>
            <Form.Select value={assetType} onChange={(e) => setAssetType(e.target.value)}>
              <option value="STOCK">STOCK</option>
              <option value="FX">FX</option>
              <option value="BOND">BOND</option>
              <option value="COMMODITY">COMMODITY</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="ch-field">
            <Form.Label>Execution side</Form.Label>
            <Form.Select value={executionType} onChange={(e) => setExecutionType(e.target.value)}>
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="ch-field">
            <Form.Label>Expiration (optional)</Form.Label>
            <Form.Control
              placeholder='ISO string or "None"'
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
          </Form.Group>
        </div>
      </section>

      {showForwardPoints && (
        <section className="ch-card">
          <div className="ch-curve-head">
            <div>
              <p className="ch-eyebrow">Forward curve</p>
              <h3 className="ch-title" style={{ fontSize: "0.95rem" }}>
                Tenor profile for {currencyPair}
              </h3>
            </div>
            <div className="ch-curve-meta">
              <span>{tenorList.length} tenors</span>
              <button
                type="button"
                className="ch-btn is-sm"
                onClick={() => setIsForwardCurveExpanded((previous) => !previous)}
              >
                {isForwardCurveExpanded ? "Collapse" : "Expand"}
              </button>
            </div>
          </div>

          {isForwardCurveExpanded ? (
            <div className="ch-curve-body">
              <CoTraderBarChart
                selectedSymbol={currencyPair}
                setTenorList={setTenorList}
                tenor={tenorList}
                isDark={isDark}
              />
            </div>
          ) : null}
        </section>
      )}

      <CoHedgerSummaryTileComponent
        summary={summary}
        loading={loading}
        currencyPair={currencyPair}
        assetType={assetType}
        executionType={executionType}
        expirationDate={expirationDate}
      />
    </div>
  );
};

export default CoHedgerSummaryDashboardTile;
